// Typed query-key factory. Use these so mutations can invalidate
// cleanly (e.g. after a POST tenant: queryClient.invalidateQueries({
// queryKey: qk.tenants(accountSlug) })).
//
// Key shape mirrors the URL nesting; the `as const` tuples give
// TanStack Query stable equality without us writing hashes.

import type { AuditLogFilters } from "./types/auditLog";
import type { RunListFilters } from "./types/onboardingRun";

export const qk = {
  // Accounts
  accounts: () => ["accounts"] as const,
  account: (a: string) => ["accounts", a] as const,

  // Tenants (nested)
  tenants: (a: string) => ["accounts", a, "tenants"] as const,
  tenant: (a: string, t: string) => ["accounts", a, "tenants", t] as const,

  // TenantSecrets
  tenantSecrets: (a: string, t: string) =>
    ["accounts", a, "tenants", t, "secrets"] as const,
  tenantSecret: (a: string, t: string, secretId: string) =>
    ["accounts", a, "tenants", t, "secrets", secretId] as const,

  // HaInstances (list only — no detail)
  haInstances: (a: string, t: string) =>
    ["accounts", a, "tenants", t, "ha-instances"] as const,

  // Sites
  sites: (a: string, t: string) =>
    ["accounts", a, "tenants", t, "sites"] as const,
  site: (a: string, t: string, s: string) =>
    ["accounts", a, "tenants", t, "sites", s] as const,

  // Floors
  floors: (a: string, t: string, s: string) =>
    ["accounts", a, "tenants", t, "sites", s, "floors"] as const,
  floor: (a: string, t: string, s: string, f: string) =>
    ["accounts", a, "tenants", t, "sites", s, "floors", f] as const,

  // Areas (list only — no detail)
  areas: (a: string, t: string, s: string, f: string) =>
    ["accounts", a, "tenants", t, "sites", s, "floors", f, "areas"] as const,

  // Logical groups (list + members; inline selection via ?groupSlug=)
  // Devices (B.2 — site-scoped, created via layout import)
  devices: (a: string, t: string, s: string) =>
    ["accounts", a, "tenants", t, "sites", s, "devices"] as const,
  device: (a: string, t: string, s: string, d: string) =>
    ["accounts", a, "tenants", t, "sites", s, "devices", d] as const,

  logicalGroups: (a: string, t: string, s: string) =>
    ["accounts", a, "tenants", t, "sites", s, "logical-groups"] as const,
  logicalGroupMembers: (a: string, t: string, s: string, g: string) =>
    [
      "accounts",
      a,
      "tenants",
      t,
      "sites",
      s,
      "logical-groups",
      g,
      "members",
    ] as const,

  // Catalogs
  spaceTypes: () => ["space-types"] as const,

  // Audit (filter object is part of the key so each filter combo caches
  // independently; pass `filters` exactly as it'll be serialised to URL).
  audit: (filters: AuditLogFilters) => ["audit", filters] as const,

  // Allocation (Program C)
  allocationGate: () => ["allocation", "gate"] as const,
  allocationLedger: (accountSlug?: string) =>
    ["allocation", "ledger", accountSlug ?? "all"] as const,
  allocationPreview: (kind: string, key: string) =>
    ["allocation", "preview", kind, key] as const,
  vlanTemplate: () => ["allocation", "vlan-template"] as const,
  allocationImports: () => ["allocation", "imports"] as const,
  allocationImport: (id: string) => ["allocation", "imports", id] as const,

  // Site network (Program C / sub-project B) — router + firewall, keyed by
  // bare site_slug. VLAN-name selectors reuse allocationLedger + vlanTemplate
  // above (no dedicated key — see features/siteNetwork/useSiteVlanNames).
  siteRouter: (s: string) => ["site-network", s, "router"] as const,
  siteFirewallPolicy: (s: string) =>
    ["site-network", s, "firewall-policy"] as const,
  siteFirewallRules: (s: string) =>
    ["site-network", s, "firewall-rules"] as const,
  // Switches (sub-project C) — keyed by bare site_slug + switch slug.
  siteSwitches: (s: string) => ["site-network", s, "switches"] as const,
  siteSwitch: (s: string, switchSlug: string) =>
    ["site-network", s, "switches", switchSlug] as const,
  siteSwitchPorts: (s: string, switchSlug: string) =>
    ["site-network", s, "switches", switchSlug, "ports"] as const,
  // Computed network-defaults proposal (not persisted; fetched on demand).
  networkDefaults: (s: string) => ["site-network", s, "network-defaults"] as const,

  // Identity roster (per tenant; C.2b-identity)
  identityMembers: (tenantSlug: string) =>
    ["identity-members", tenantSlug] as const,

  // Site access (C.2b-access) — publication + netbird, keyed by bare site_slug.
  sitePublication: (s: string) => ["site-access", s, "publication"] as const,
  siteNetbird: (s: string) => ["site-access", s, "netbird"] as const,

  // Site hosts (C.2b-svc/hosts) — service-profile (1:1) + managed hosts (N).
  siteServiceProfile: (s: string) => ["site-hosts", s, "service-profile"] as const,
  siteHosts: (s: string) => ["site-hosts", s, "hosts"] as const,
  siteHost: (s: string, hostSlug: string) => ["site-hosts", s, "hosts", hostSlug] as const,

  // Onboarding cockpit (F1) — request intent (1:1) + content-addressed artifact.
  onboardingRequest: (s: string) => ["site-onboarding", s, "request"] as const,
  renderArtifact: (sha: string) => ["render", "artifact", sha] as const,

  // Onboarding runs (F2) — list keyed by the filter object so each combo caches
  // independently; detail keyed by run id.
  onboardingRuns: (filters: RunListFilters) => ["onboarding-runs", filters] as const,
  onboardingRun: (id: string) => ["onboarding-runs", "detail", id] as const,
};
